import React, { useEffect, useState, useRef } from "react";
import {
  Table,
  Input,
  Button,
  Popconfirm,
  Form,
  Space,
  Col,
  Row,
  message,
  Select,
  Checkbox,
  DatePicker,
} from "antd";
import { useDispatch, useSelector } from "react-redux";
import { PlusOutlined, CloseOutlined } from "@ant-design/icons";
import { ReactComponent as EditIcon } from "../../assets/edit-icon.svg";
import { ReactComponent as SaveIcon } from "../../assets/save-icon.svg";
import { ReactComponent as CrossIcon } from "../../assets/cross-icon.svg";
import SpinnerComponent from "../../components/spinner";
import _ from "lodash";
import moment from "moment";

import {
  createReportDetail,
  fetchReportDetail,
  isAddingAction,
  updateReportDetail,
  setStorage,
} from "./redux/actions";

const { Option } = Select;
const { RangePicker } = DatePicker;

const ReportDetailTable = ({ canUpdate = true, canDelete = false }) => {
  const [editingLoading, setEditingLoading] = useState(false);
  const [editingKey, setEditingKey] = useState("");
  const [search, setSearch] = useState({});
  const [req, setReq] = useState(false);
  const [data, setData] = useState([]);
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const ref = useRef(null);
  const [defaultPageOneReq, setDefaultPageOneReq] = useState(false);
  const [datePickerOpened, setDatePickerOpened] = useState(false);
  const [date, setDate] = useState(null);
  const isEditing = (record) => record._id === editingKey;
  const [dateRange, setDateRange] = useState([]);

  const { allReportDetail, fetchingLoading, updateLoading, isAdding, ...rest } =
    useSelector((state) => state.reportDetailReducer);

  const [debouncedCallApi] = useState(() =>
    _.debounce(() => setReq((prev) => !prev), 500)
  );

  const reportTypeOptions = [
    { label: "TERM", value: "term" },
    { label: "SEMESTER", value: "semester" },
  ];

  useEffect(() => {
    const localStorageExist = localStorage.getItem("reportDetailManagerState");
    if (localStorageExist) {
      const localStorageData = JSON.parse(localStorageExist);

      if (localStorageData) {
        setTimeout(() => {
          setSearch(localStorageData.search);
          dispatch(setStorage(localStorageData));

          debouncedCallApi();
        }, 1000);
      }
    }
  }, []);

  useEffect(() => {
    dispatch(fetchReportDetail());

    return () => {
      dispatch(isAddingAction(false));
    };
  }, []);

  useEffect(() => {
    setEditingLoading(updateLoading);
    if (!updateLoading) {
      setEditingKey("");
    }
  }, [updateLoading]);

  useEffect(() => {
    console.log(" allReportDetail updated", allReportDetail);
    setData(allReportDetail);
  }, [allReportDetail]);

  console.log("isAdding button", isAdding);

  useEffect(() => {
    let filteredData = allReportDetail;

    if (search?.title?.length > 0) {
      filteredData = filteredData.filter((el) =>
        el.title?.toLowerCase()?.includes(search.title?.toLowerCase())
      );
    }

    if (search?.report_type?.length > 0) {
      filteredData = filteredData.filter((el) =>
        el.report_type
          ?.toLowerCase()
          ?.includes(search.report_type?.toLowerCase())
      );
    }

    if (search?.dateRange?.start_date && search?.dateRange?.end_date) {
      const start = moment(search.dateRange.start_date);
      const end = moment(search.dateRange.end_date);

      filteredData = filteredData.filter((el) => {
        const elStart = moment(el.start_date);
        const elEnd = moment(el.end_date);

        return (
          elStart.isSameOrBefore(end, "day") &&
          elEnd.isSameOrAfter(start, "day")
        );
      });
    }

    if (
      search?.include_passed_curriculums === true ||
      search?.include_passed_curriculums === false
    ) {
      filteredData = filteredData.filter(
        (el) =>
          el.include_passed_curriculums === search.include_passed_curriculums
      );
    }

    setData(filteredData);
  }, [req]);

  useEffect(() => {
    const isSearchChanged =
      JSON.stringify(search) !== JSON.stringify(rest.search);

    if (isSearchChanged) {
      setSearch(rest.search);
    }

    if (isSearchChanged) {
      debouncedCallApi();
    }
  }, []);

  const formatDates = (values) => ({
    start_date: values.start_date?.[0]?.format("YYYY-MM-DD") || "",
    end_date: values.start_date?.[1]?.format("YYYY-MM-DD") || "",
  });

  const EditableCell = ({
    editing,
    dataIndex,
    title,
    inputType,
    record,
    index,
    children,
    ...restProps
  }) => {
    const textInput = (
      <Input
        placeholder="Insert Report Name"
        size="large"
        ref={ref}
        onKeyPress={(e) => {
          if (e.key === "Enter") {
            save(record._id);
          }
        }}
      />
    );

    let inputNode = null;
    let formItemProps = { name: dataIndex, style: { margin: 0 } };

    if (dataIndex === "title") {
      return (
        <td {...restProps}>
          {editing ? (
            <Form.Item
              {...formItemProps}
              rules={[
                { required: true, message: "This field is required!" },
                { min: 1, message: "Invalid!" },
              ]}
            >
              {textInput}
            </Form.Item>
          ) : (
            children
          )}
        </td>
      );
    }

    if (dataIndex === "report_type") {
      inputNode = (
        <Select placeholder="Select Report Type">
          {reportTypeOptions.map((option) => (
            <Option key={option.value} value={option.value}>
              {option.label}
            </Option>
          ))}
        </Select>
      );
    }

    if (dataIndex === "start_date") {
      inputNode = (
        <RangePicker
          format="YYYY-MM-DD"
          style={{ width: "100%" }}
          suffixIcon={null}
          size="large"
        />
      );
    }

    if (dataIndex === "include_passed_curriculums") {
      inputNode = <Checkbox />;
      formItemProps.valuePropName = "checked";
    }

    return (
      <td {...restProps}>
        {editing ? (
          <Form.Item
            {...formItemProps}
            rules={[{ required: true, message: "Required!" }]}
          >
            {inputNode}
          </Form.Item>
        ) : (
          children
        )}
      </td>
    );
  };

  const edit = (record) => {
    dispatch(isAddingAction(true));

    form.setFieldsValue({
      ...record,
      start_date:
        record.start_date && record.end_date
          ? [moment(record.start_date), moment(record.end_date)]
          : [],
    });

    setEditingKey(record._id);
    setTimeout(() => {
      ref.current?.focus();
    }, 300);
  };

  const cancel = (record) => {
    dispatch(isAddingAction(false));
    form.resetFields();
    setEditingKey("");
    if (record?.isAdd) {
      return setData((data) => data.filter((el) => el._id !== record._id));
    } else {
      setData(data);
    }
  };

  const save = async (id) => {
    try {
      const row = await form.validateFields();
      const newData = [...data];
      const index = newData.findIndex((item) => id === item._id);

      if (index > -1) {
        const item = newData[index];

        if (item?.isAdd) {
          form.submit();
        } else {
          if (isAlreadyExist({ ...item, ...row })) {
            return message.error("Report title already exists!");
          }

          const payload = {
            id: id,
            title: row.title.trim(),
            report_type: row.report_type,
            include_passed_curriculums: !!row.include_passed_curriculums,
            ...formatDates(row),
          };

          dispatch(updateReportDetail(payload));
          dispatch(isAddingAction(false));
          setEditingKey("");
        }
      }
    } catch (errInfo) {
      console.error("Validate Failed:", errInfo);
    }
  };

  const handleAdd = () => {
    dispatch(isAddingAction(true));
    const _id = Date.now();

    const newData = {
      _id,
      title: "",
      report_type: "",
      start_date: "",
      end_date: "",
      include_passed_curriculums: false,
      isAdd: true,
    };

    setData((prev) => [newData, ...(Array.isArray(prev) ? prev : [])]);
    setEditingKey(_id);
    edit(newData);
  };

  const isAlreadyExist = (record) => {
    const { title } = record;
    const alreadyExist = data.filter(
      (el) => el.title?.trim()?.toLowerCase() === title?.trim()?.toLowerCase()
    );

    return alreadyExist.length > 0 && record._id !== alreadyExist[0]?._id;
  };

  const handleFinish = (values) => {
    if (isAlreadyExist(values)) {
      return message.error("Report title already exists!");
    }

    const payload = {
      id: values._id || values.id,
      title: values.title.trim(),
      report_type: values.report_type,
      include_passed_curriculums: !!values.include_passed_curriculums,
      ...formatDates(values),
    };

    // if (values.isAdd || !payload.id) {
    dispatch(createReportDetail(payload));
    // } else {
    //   dispatch(updateReportDetail(payload));
    // }

    dispatch(isAddingAction(false));

    setEditingKey("");
  };

  const handleSetStorage = (key, value) => {
    dispatch(setStorage({ [key]: value }));
  };

  const handleSearch = (key, value, func = null) => {
    let updatedState = {
      ...search,
      [key]: value,
    };

    setSearch(updatedState);

    if (typeof func === "function") {
      func();
    }

    dispatch(setStorage({ search: updatedState }));
    handleSetStorage("search", updatedState);
  };

  const searchInput = (key) => (
    <Input
      onClick={(e) => e.stopPropagation()}
      value={search[key]}
      onChange={(e) => {
        handleSearch(key, e.target.value, debouncedCallApi);
      }}
    />
  );

  const columns = [
    {
      title: () => (
        <div align="top" style={{ width: "100%" }}>
          <div className="special-table-header-text">Report Name</div>
          <div>{searchInput("title")}</div>
        </div>
      ),
      dataIndex: "title",
      key: "title",
      width: "20",
      editable: true,
      sorter: (a, b) => a.title.localeCompare(b.title),
    },

    {
      title: () => (
        <div align="top" style={{ width: "100%" }}>
          <div className="special-table-header-text">Report Type</div>
          <Select
            style={{ width: "100%" }}
            onChange={(value) =>
              handleSearch("report_type", value, debouncedCallApi)
            }
            allowClear
            showSearch
            size="small"
            value={search?.report_type}
          >
            {reportTypeOptions.map((option) => (
              <Select.Option key={option.value} value={option.value}>
                {option.label}
              </Select.Option>
            ))}
          </Select>
        </div>
      ),
      dataIndex: "report_type",
      key: "report_type",
      editable: true,
      sorter: (a, b) =>
        (a.report_type || "").localeCompare(b.report_type || ""),
      render: (value) => (value ? value.toUpperCase() : ""),
    },
    {
      title: () => (
        <div align="top" style={{ width: "100%" }}>
          <div className="special-table-header-text">Date Range</div>
          <RangePicker
            value={[
              date?.start_date ? moment(date.start_date) : null,
              date?.end_date ? moment(date.end_date) : null,
            ]}
            onChange={(dates) => {
              if (dates && dates.length === 2) {
                const range = {
                  start_date: dates[0].format("YYYY-MM-DD"),
                  end_date: dates[1].format("YYYY-MM-DD"),
                };
                setDate(range);
                handleSearch("dateRange", range, debouncedCallApi);
              } else {
                setDate({ start_date: null, end_date: null });
                handleSearch("dateRange", {}, debouncedCallApi);
              }
            }}
            format="YYYY-MM-DD"
            style={{ width: "100%" }}
            suffixIcon={null}
          />
        </div>
      ),
      dataIndex: "start_date",
      key: "start_date",
      editable: true,
      sorter: (a, b) => {
        const aStart = moment(a.start_date);
        const bStart = moment(b.start_date);

        if (aStart.isSame(bStart)) {
          return moment(a.end_date).diff(moment(b.end_date));
        }
        return aStart.diff(bStart);
      },
      sortDirections: ["ascend", "descend"],
      width: "40",
      align: "center",
      render: (start_date, record) => {
        if (!start_date || !record?.end_date) return null;
        const formattedStart = moment(start_date).format("DD-MMM-YYYY");
        const formattedEnd = moment(record.end_date).format("DD-MMM-YYYY");
        return <div>{`${formattedStart} - ${formattedEnd}`}</div>;
      },
    },
    {
      title: () => {
        return (
          <div align="top" style={{ width: "100%" }}>
            <div className="special-table-header-text">
              Include Passed Curriculum
            </div>
            <div>
              <Select
                style={{
                  width: "100%",

                  textTransform: "capitalize",
                }}
                size="small"
                onClick={(e) => e.stopPropagation()}
                allowClear={true}
                readOnly={true}
                className="Select Gender"
                dropdownClassName="select-dropdown-custom"
                getPopupContainer={() =>
                  document.getElementsByClassName(
                    "ant-layout-content layout-content site-layout-background custom-textarea"
                  )[0]
                }
                showSearch
                placeholder=""
                value={search["include_passed_curriculums"]}
                optionFilterProp="children"
                onChange={(e) => {
                  handleSearch(
                    "include_passed_curriculums",
                    e,
                    debouncedCallApi
                  );
                }}
                optionLabelProp="label"
                filterOption={(input, option) =>
                  option?.children
                    ?.toLowerCase()
                    .indexOf(input.toLowerCase()) >= 0
                }
              >
                {[
                  { label: "NO", value: false },
                  { label: "YES", value: true },
                ]?.map(({ label, value }) => {
                  return (
                    <Option value={value} label={label}>
                      {label || ""}
                    </Option>
                  );
                })}
              </Select>
            </div>
          </div>
        );
      },
      dataIndex: "include_passed_curriculums",
      key: "include_passed_curriculums",
      width: "auto",
      editable: true,
      align: "center",
      sorter: (a, b) =>
        a.include_passed_curriculums - b.include_passed_curriculums,
      render: (include_passed_curriculums) => {
        return (
          <div style={{ paddingLeft: "12px" }}>
            {include_passed_curriculums ? "YES" : "NO"}
          </div>
        );
      },
    },

    {
      title: "Action",
      key: "action",
      dataIndex: "",
      width: "20",
      align: "center",
      render: (_, record) => {
        if (!canUpdate && !canDelete) {
          return;
        }

        const editable = isEditing(record);
        return editable ? (
          editingLoading && editingKey === record._id ? (
            <SpinnerComponent fontSize={14} />
          ) : (
            <Space size={20} align="center">
              <SaveIcon
                className="custom-icon"
                onClick={() => save(record._id)}
              />
              <Popconfirm
                title="Sure to cancel?"
                onConfirm={() => cancel(record)}
              >
                <CrossIcon
                  style={{ height: "20px", width: "20px" }}
                  className="custom-icon"
                />
              </Popconfirm>
            </Space>
          )
        ) : (
          <Space size={20} align="center">
            {canUpdate && (
              <EditIcon
                className={`custom-icon ${isAdding ? "disable-icon" : ""}`}
                disabled={editingKey !== ""}
                onClick={() => !isAdding && edit(record)}
              />
            )}
          </Space>
        );
      },
    },
  ];

  const mergedColumns = columns.map((col) => {
    if (!col.editable) {
      return col;
    }

    return {
      ...col,
      onCell: (record) => ({
        record,
        inputType: "text",
        dataIndex: col.dataIndex,
        editing: isEditing(record),
      }),
    };
  });

  return (
    <div className="page-top-div">
      <span className="text-span">Report Period</span>
      <Row justify="center" className="production-manager-parent-row">
        <Col xxl={24} xl={24} lg={24} md={24} className="heading-col">
          <Row align="middle" gutter={[30, 0]}>
            <Col>
              <div>
                <span className="heading-span">Report Period Register</span>
              </div>
            </Col>

            <Col>
              {canUpdate && (
                <Button
                  icon={<PlusOutlined />}
                  // className='add-status-btn'
                  size="medium"
                  disabled={isAdding}
                  onClick={handleAdd}
                >
                  Create Report
                </Button>
              )}
            </Col>
            <Col>
              <Button
                onClick={() => {
                  handleSetStorage("search", {});
                  setSearch({});
                  debouncedCallApi();
                }}
                icon={<CloseOutlined />}
                className="add-user-btn"
                size="middle"
              >
                Clear Filters
              </Button>
            </Col>
          </Row>
        </Col>

        <Col lg={24} xl={24} xxl={24} md={24} style={{ minHeight: "70vh" }}>
          <Form
            form={form}
            component={false}
            onFinish={handleFinish}
            className="table-style"
          >
            <Table
              className="special-table activity-type-table project-statuses-table"
              components={{
                body: {
                  cell: EditableCell,
                },
              }}
              scroll={{ x: "max-content" }}
              rowClassName="editable-row"
              dataSource={data}
              loading={fetchingLoading}
              columns={mergedColumns}
              pagination={false}
              rowKey="_id"
            />
          </Form>
        </Col>
      </Row>
    </div>
  );
};

export default ReportDetailTable;
